import React, { useState, useEffect } from 'react';
import Header from '../ui/Header';
import { triggerHaptic } from '../../utils/haptics';
import { UserPlus, Search, UserX } from 'lucide-react';
import api from '../../api';
import { NoAutoFillInput } from '../ui/NoAutoFillInput';

const ContactsView = ({ t, onBack, userId, onChatWithFriend }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [newFriendId, setNewFriendId] = useState('');

  useEffect(() => {
    loadFriends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadFriends = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const data = await api.getFriends(userId);
      setFriends(data);
    } catch (err) {
      console.error('加载好友列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearchUser = async () => {
    if (!newFriendId.trim()) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const user = await api.searchUser(newFriendId.trim());
      setSearchResult(user);
    } catch (err) {
      alert('搜索用户失败: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleAddFriend = async () => {
    if (!newFriendId.trim()) return;
    try {
      await api.addFriend(userId, newFriendId.trim());
      alert('好友请求已发送');
      setNewFriendId('');
      setSearchResult(null);
      setShowAddFriend(false);
    } catch (err) {
      alert('添加好友失败: ' + err.message);
    }
  };

  const handleDeleteFriend = async (friendId) => {
    if (!window.confirm('确定要删除该好友吗？')) return;
    try {
      await api.deleteFriend(userId, friendId);
      setFriends(prev => prev.filter(f => f.friendId !== friendId));
    } catch (err) {
      alert('删除好友失败: ' + err.message);
    }
  };

  const filteredFriends = friends.filter(f => 
    (f.friendEmail || f.friendId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-in slide-in-from-right duration-300">
      <div className="flex items-center justify-between">
        <Header title={t.contacts} t={t} onBack={onBack} />
        <button
          onClick={() => setShowAddFriend(!showAddFriend)}
          className="p-2 rounded-full bg-[var(--neon-primary)] text-black hover:opacity-80 transition-opacity"
        >
          <UserPlus size={20} />
        </button>
      </div>

      {showAddFriend && (
        <div className="p-4 glass-card rounded-2xl space-y-3">
          <div className="flex gap-2">
            <NoAutoFillInput
              type="text"
              inputMode="text"
              value={newFriendId}
              onChange={(e) => { setNewFriendId(e.target.value); setSearchResult(null); }}
              placeholder="输入用户ID搜索"
              name="friend-search"
              id="friend-search"
              className="flex-1 px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-[var(--neon-primary)]"
            />
            <button
              onClick={handleSearchUser}
              disabled={searching || !newFriendId.trim()}
              className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              {searching ? '搜索中...' : '搜索'}
            </button>
          </div>
          
          {searchResult && (
            <div className="p-3 bg-white/5 rounded-xl border border-[var(--neon-primary)]/30">
              <div className="flex items-center gap-3">
                <img 
                  src={searchResult.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${searchResult.uid}`} 
                  alt={searchResult.displayName || searchResult.email}
                  className="w-10 h-10 rounded-full bg-black"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold truncate">
                    {searchResult.displayName || searchResult.email || searchResult.uid}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{searchResult.email || searchResult.uid}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={() => { setShowAddFriend(false); setSearchResult(null); setNewFriendId(''); }}
              className="flex-1 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
            >
              取消
            </button>
            <button
              onClick={async () => {
                if (!newFriendId.trim() || searching) return;
                
                const targetUserId = newFriendId.trim();
                setSearching(true);
                
                try {
                  if (!searchResult) {
                    await api.searchUser(targetUserId);
                  }
                  await api.addFriend(userId, targetUserId);
                  alert('好友请求已发送');
                  setNewFriendId('');
                  setSearchResult(null);
                  setShowAddFriend(false);
                } catch (err) {
                  alert('添加好友失败: ' + err.message);
                } finally {
                  setSearching(false);
                }
              }}
              disabled={!newFriendId.trim() || searching}
              className="flex-1 py-2 rounded-xl bg-[var(--neon-primary)] text-black text-sm font-bold hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searching ? '处理中...' : '添加好友'}
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="搜索好友..."
          className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-[var(--neon-primary)]"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">
          <p>加载中...</p>
        </div>
      ) : filteredFriends.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <p>{searchQuery ? '未找到匹配的好友' : '暂无好友，点击右上角添加'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFriends.map(friend => (
            <div
              key={friend.id}
              className="flex items-center gap-4 p-4 glass-card rounded-2xl hover:bg-white/5 transition-colors group"
            >
              <div
                className="flex-1 flex items-center gap-4 cursor-pointer"
                onClick={() => {
                  triggerHaptic();
                  onChatWithFriend(friend);
                }}
              >
                <img
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${friend.friendId}`}
                  className="w-12 h-12 rounded-full bg-black border-2 border-white/10 group-hover:border-[var(--neon-primary)] shadow-lg"
                  alt={friend.friendEmail || friend.friendId}
                />
                <div className="flex-1">
                  <span className="text-base font-bold text-white group-hover:text-[var(--neon-primary)] transition-colors block">
                    {friend.friendEmail || friend.friendId.slice(0, 8)}
                  </span>
                  <span className="text-xs text-gray-400">
                    点击开始聊天
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleDeleteFriend(friend.friendId)}
                className="p-2 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                title="删除好友"
              >
                <UserX size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsView;
